import { useState, useCallback } from 'react';
import { BACKEND_URL } from '../config/appConfig';
import { track, trackUpgradeCTA, EVENTS } from '../utils/analytics';
import debug from '../utils/debug';

/**
 * useUpgradeFlow — Manages upgrade modals, paywall, subscription tiers,
 * and Stripe checkout flow. Extracted from App.js Phase 5 Heavy Batch Round 1.
 */
export default function useUpgradeFlow({ userId, isPremium, setCurrentView }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState(null);
  const [subscriptionTiers, setSubscriptionTiers] = useState(null);
  const [showPremiumTrackingModal, setShowPremiumTrackingModal] = useState(false);
  const [homeCheckingOut, setHomeCheckingOut] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState('');

  // ── Show upgrade modal with context ────────────────────────────────────────
  const showUpgrade = useCallback((feature) => {
    setUpgradeContext(feature);
    setShowUpgradeModal(true);
    track(EVENTS.UPGRADE_MODAL_OPENED, { feature });
  }, []);

  // ── Direct Stripe checkout from Home screen ────────────────────────────────
  const handleHomeUpgrade = useCallback(async (planKey) => {
    if (!userId || homeCheckingOut) return;

    const tier = planKey === 'smart_plaite_monthly' ? 'smart' : 'genius';
    setHomeCheckingOut(tier);

    trackUpgradeCTA('home', tier === 'smart' ? 'smart_plaite' : 'genius_plaite');
    track(EVENTS.CHECKOUT_STARTED, { plan: planKey, location: 'home' });

    try {
      debug.log('[App] Creating checkout session for:', planKey);

      const response = await fetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          price_key: planKey,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      debug.log('[App] Checkout session created:', data);

      const checkoutUrl = data.checkout_url || data.session_url;
      if (checkoutUrl) {
        debug.log('[App] Redirecting to Stripe:', checkoutUrl);
        window.location.href = checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('[App] Checkout error:', err);
      alert(`Unable to start checkout: ${err.message}`);
      setHomeCheckingOut(null);
    }
  }, [userId, homeCheckingOut]);

  // ── Check premium access and show paywall if needed ────────────────────────
  const requirePremium = useCallback((featureName) => {
    if (isPremium) return true;
    setPaywallFeature(featureName);
    setShowPaywall(true);
    return false;
  }, [isPremium]);

  // ── Navigate to settings for premium upgrade ──────────────────────────────
  const handlePremiumUpgrade = useCallback(() => {
    setCurrentView('settings');
  }, [setCurrentView]);

  // ── Load subscription tiers (for pricing display) ──────────────────────────
  const loadSubscriptionTiers = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/subscription/tiers`);
      if (response.ok) {
        const data = await response.json();
        setSubscriptionTiers(data);
      }
    } catch (error) {
      console.error('Error loading subscription tiers:', error);
    }
  }, []);

  return {
    showUpgradeModal, setShowUpgradeModal,
    upgradeContext,
    subscriptionTiers,
    showPremiumTrackingModal, setShowPremiumTrackingModal,
    homeCheckingOut, setHomeCheckingOut,
    showPaywall, setShowPaywall,
    paywallFeature,
    showUpgrade,
    handleHomeUpgrade,
    requirePremium,
    handlePremiumUpgrade,
    loadSubscriptionTiers
  };
}
